import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Input, Modal, Select } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { ROLES, ROLE_LABEL, createUserSchema, type CreateUserInput } from '../schemas';
import { useCreateUser } from '../api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateUserModal({ open, onOpenChange }: Props) {
  const create = useCreateUser();
  const form = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { name: '', email: '', cargo: '', role: 'colaborador' },
  });

  useEffect(() => {
    if (!open) {
      form.reset();
      create.reset();
    }
  }, [open, form, create]);

  const tempPassword = create.data?.temp_password;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Convidar usuário"
      size="lg"
      footer={
        tempPassword ? (
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => void form.handleSubmit((v) => create.mutate(v))()}
              disabled={create.isPending}
            >
              {create.isPending ? 'Criando…' : 'Criar usuário'}
            </Button>
          </>
        )
      }
    >
      {tempPassword ? (
        <Stack gap="sm">
          <p className="text-sm text-text">
            Usuário <strong>{create.data?.name}</strong> criado.
          </p>
          <p className="text-xs text-text-2">
            Senha temporária — copie e envie ao usuário. Ela não será exibida novamente.
          </p>
          <code className="block rounded-md bg-surface px-3 py-2 font-mono text-sm select-all">
            {tempPassword}
          </code>
        </Stack>
      ) : (
        <form className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
          <Stack gap="md">
            <Field
              label="Nome completo"
              htmlFor="usr-name"
              error={form.formState.errors.name?.message}
              required
            >
              <Input
                id="usr-name"
                invalid={!!form.formState.errors.name}
                {...form.register('name')}
              />
            </Field>
            <Field
              label="E-mail"
              htmlFor="usr-email"
              error={form.formState.errors.email?.message}
              required
            >
              <Input
                id="usr-email"
                type="email"
                invalid={!!form.formState.errors.email}
                {...form.register('email')}
              />
            </Field>
            <Field
              label="Cargo"
              htmlFor="usr-cargo"
              error={form.formState.errors.cargo?.message}
            >
              <Input id="usr-cargo" {...form.register('cargo')} />
            </Field>
            <Field
              label="Papel"
              htmlFor="usr-role"
              error={form.formState.errors.role?.message}
              required
            >
              <Select id="usr-role" {...form.register('role')}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </Select>
            </Field>
          </Stack>
          {create.error && (
            <p className="text-xs text-danger">
              {create.error instanceof ApiError ? create.error.message : 'Falha ao criar usuário'}
            </p>
          )}
        </form>
      )}
    </Modal>
  );
}
